import React, { useEffect, useRef } from 'react'
import ProfileModalSectionRender from './profileModalSectionRender';


function ProfileModalView({ sections, sectionRefs, pl='' }) {
    return (
        <>
            {sections.map((section, index) => (
                <div
                    key={section.key}
                    ref={sectionRefs[index]}
                    className="profile-review-section-shell inline-block h-full min-w-full align-top px-2 py-2 text-left sm:px-4"
                >
                    <ProfileModalSectionRender
                        ref={sectionRefs[index]}
                        name={section.key}
                        data={section.content}
                        pl={pl}
                    />
                </div>
            ))}
        </>
    );
}


export default function ProfileModal({ modalProfile, handleModalClose }) {
    const scrollContainerRef = useRef(null);

    const renderOrder = [
        'manager',
        'employees',
        'license_states',
        'processing_plan',
        'margin_management',
        'branch_location',
        'locking_cabinet',
        'local_it_company',
        'office_space',
        'mortgage_sign',
        'compliance_signage',
        'occupational_license',
        'marketing_details',
    ];
    
    // Extract all sections from the profile data
    const data = modalProfile?.profile?.data || {};
    const allSections = Object.keys(data);
    
    // Sort sections based on the renderOrder
    const sortedSections = renderOrder.filter((section) => allSections.includes(section));
    
    // Create sections array with keys and content
    const sections = sortedSections.map((key) => ({
        key,
        content: data[key],
    }));
    
    // Create refs for each section
    const sectionRefs = sections.map(() => React.createRef());

    useEffect(() => {
        // Disable background scrolling when modal is open
        document.body.style.overflow = 'hidden';

        // Re-enable scrolling when modal is closed
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []); // Empty dependency array ensures this runs on mount and unmount
    

    const handleScroll = (direction) => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const sectionElements = sectionRefs.map((ref) => ref.current);
    
            // Calculate cumulative scroll positions
            const sectionPositions = [];
            let cumulativeWidth = 0;
    
            sectionElements.forEach((section) => {
                sectionPositions.push(cumulativeWidth);
                cumulativeWidth += section.offsetWidth;
            });
    
            const containerScrollLeft = container.scrollLeft;
    
            // Determine current section index
            let currentSectionIndex = 0;
    
            for (let i = sectionPositions.length - 1; i >= 0; i--) {
                if (containerScrollLeft >= sectionPositions[i]) {
                    currentSectionIndex = i;
                    break;
                }
            }
    
            let targetIndex = currentSectionIndex;
    
            if (direction === 'left') {
                targetIndex = Math.max(0, currentSectionIndex - 1);
            } else if (direction === 'right') {
                targetIndex = Math.min(sections.length - 1, currentSectionIndex + 1);
            }
    
            // Scroll to the target section
            const targetScrollLeft = sectionPositions[targetIndex];
            container.scrollTo({
                left: targetScrollLeft,
                behavior: 'smooth',
            });
    
            // Reset vertical scroll of the target section
            const targetSectionRef = sectionRefs[targetIndex];
            if (targetSectionRef && targetSectionRef.current) {
                targetSectionRef.current.scrollTop = 0;
            }
        }
    };

    return (
        <div
            tabIndex="-1"
            className="fixed inset-0 z-[70] flex h-full w-full items-center justify-center bg-[#211b4f]/45 p-3 backdrop-blur-sm sm:p-6"
        >
            <div className="relative flex h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                    <header className="sticky top-0 z-20 flex items-center justify-end gap-4 border-b border-slate-200 bg-white/95 px-4 py-3.5 backdrop-blur sm:px-5">
                    <button
                        type="button"
                        className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200 hover:text-[#211b4f] focus:outline-none focus:ring-2 focus:ring-[#fb6635]"
                        onClick={handleModalClose}
                        aria-label="Close modal"
                    >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 14 14" aria-hidden="true">
                            <path
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M1 1l6 6m0 0l6 6M7 7l6-6M7 7L1 13"
                            />
                        </svg>
                    </button>
                    </header>

                    <div className="relative flex min-h-0 flex-1 items-center">
                        <button
                            type="button"
                            onClick={() => handleScroll('left')}
                            className="absolute left-2 top-1/2 z-10 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-md transition-colors hover:bg-slate-100 hover:text-[#211b4f]"
                            aria-label="Previous section"
                        >
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <div
                            className="h-full w-full min-w-0 flex-1 overflow-x-auto whitespace-nowrap scroll-smooth scrollbar-hide"
                            ref={scrollContainerRef}
                        >
                            <ProfileModalView sections={sections} sectionRefs={sectionRefs} pl={modalProfile?.profile?.data?.pl} />
                        </div>
                        <button
                            type="button"
                            onClick={() => handleScroll('right')}
                            className="absolute right-6 top-1/2 z-10 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-md transition-colors hover:bg-slate-100 hover:text-[#211b4f]"
                            aria-label="Next section"
                        >
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                    </div>
            </div>
        </div>
    );
}
